import { Item, Kiosk, Listing, Lock } from "./contracts/0x2/kiosk.mjs";
import { bcs } from "@safecoin/safe/bcs";
import { normalizeSafeAddress, normalizeStructTag, parseStructTag } from "@safecoin/safe/utils";
//#region src/utils.ts
async function getKioskObject(client, id) {
	const { object } = await client.core.getObject({
		objectId: id,
		include: { content: true }
	});
	if (!object.content) throw new Error(`Kiosk ${id} not found or has no content`);
	const parsed = Kiosk.parse(object.content);
	return {
		id: parsed.id,
		profits: parsed.profits.value.toString(),
		owner: parsed.owner,
		itemCount: parsed.item_count,
		allowExtensions: parsed.allow_extensions
	};
}
function extractKioskData(data, listings, lockedItemIds, kioskId) {
	return data.reduce((acc, val) => {
		const type = val.name.type;
		const parsedType = parseStructTag(type);
		const baseType = `${normalizeSafeAddress(parsedType.address)}::${parsedType.module}::${parsedType.name}`;
		if (baseType === "0x0000000000000000000000000000000000000000000000000000000000000002::kiosk::Item") {
			const parsed = Item.parse(val.name.bcs);
			acc.itemIds.push(parsed.id);
			acc.items.push({
				objectId: parsed.id,
				type: val.valueType,
				isLocked: false,
				kioskId
			});
		}
		if (baseType === "0x0000000000000000000000000000000000000000000000000000000000000002::kiosk::Listing") {
			const parsed = Listing.parse(val.name.bcs);
			acc.listingIds.push(val.fieldId);
			listings.push({
				objectId: parsed.id,
				listingId: val.fieldId,
				isExclusive: parsed.is_exclusive
			});
		}
		if (baseType === "0x0000000000000000000000000000000000000000000000000000000000000002::kiosk::Lock") lockedItemIds?.push(Lock.parse(val.name.bcs).id);
		if (baseType === "0x0000000000000000000000000000000000000000000000000000000000000002::kiosk_extension::ExtensionKey") acc.extensions.push({
			objectId: val.fieldId,
			type: normalizeStructTag(parsedType.typeParams[0])
		});
		return acc;
	}, {
		items: [],
		itemIds: [],
		listingIds: [],
		extensions: []
	});
}
/**
* A helper that attaches the listing prices to kiosk listings.
*/
function attachListingsAndPrices(kioskData, listings, listingObjects) {
	const itemListings = listings.reduce((acc, item, idx) => {
		acc[item.objectId] = { ...item };
		if (listingObjects.length === 0) return acc;
		const obj = listingObjects[idx];
		if (obj.content) acc[item.objectId].price = bcs.u64().parse(obj.content).toString();
		return acc;
	}, {});
	kioskData.items.forEach((item) => {
		item.listing = itemListings[item.objectId] || void 0;
	});
}
/**
* A helper that attaches object data to kiosk items.
* Works with core API objects that contain BCS content.
*/
function attachObjects(kioskData, objects) {
	const mapping = objects.reduce((acc, obj) => {
		acc[obj.objectId] = obj;
		return acc;
	}, {});
	kioskData.items.forEach((item) => {
		item.data = mapping[item.objectId] || void 0;
	});
}
/**
* A Helper to attach locked state to items in Kiosk Data.
*/
function attachLockedItems(kioskData, lockedItemIds) {
	const lockedStatuses = lockedItemIds.reduce((acc, item) => {
		acc[item] = true;
		return acc;
	}, {});
	kioskData.items.forEach((item) => {
		item.isLocked = lockedStatuses[item.objectId] || false;
	});
}
/**
* A helper to fetch all dynamic field pages.
* We need that to fetch the kiosk DFs consistently, until we have
* RPC calls that allow filtering of Type / batch fetching of spec
*/
async function getAllDynamicFields(client, parentId, pagination) {
	let hasNextPage = true;
	let cursor = null;
	const data = [];
	while (hasNextPage) {
		const result = await client.core.listDynamicFields({
			parentId,
			limit: pagination.limit || void 0,
			cursor
		});
		data.push(...result.dynamicFields);
		hasNextPage = result.hasNextPage;
		cursor = result.cursor;
	}
	return data;
}
/**
* Converts a number to basis points.
* Supports up to 2 decimal points.
* E.g 9.95 -> 995
* @param percentage A percentage amount in the range [0, 100] including decimals.
*/
function percentageToBasisPoints(percentage) {
	if (percentage < 0 || percentage > 100) throw new Error("Percentage needs to be in the [0,100] range.");
	return Math.ceil(percentage * 100);
}
function getNormalizedRuleType(rule) {
	const normalizedRuleAddress = rule.split("::");
	normalizedRuleAddress[0] = normalizeSafeAddress(normalizedRuleAddress[0]);
	return normalizedRuleAddress.join("::");
}
//#endregion
export { attachListingsAndPrices, attachLockedItems, attachObjects, extractKioskData, getAllDynamicFields, getKioskObject, getNormalizedRuleType, percentageToBasisPoints };

//# sourceMappingURL=utils.mjs.map